'use client'

import { useSignals } from '@preact/signals-react/runtime'
import { ConfiguratorEncoder } from '@susu/headless-commerce/features/configurator/domain/Configurator/ConfiguratorEncoder'
import {
  configuratorJSONSignal,
  configuratorSignal,
} from '@susu/headless-commerce/features/configurator/signals/configurator'
import { isBrowser } from '@susu/headless-commerce/utils/environment'
import log from '@susu/headless-commerce/utils/log'
import { isUndefined } from '@susu/undefined'
import { type ReactNode, useCallback, useEffect } from 'react'

export type ClientConfiguratorProps = {
  configuratorJSON: string
  children: ReactNode
}

export const ClientConfigurator = ({
  configuratorJSON,
  children,
}: ClientConfiguratorProps) => {
  useSignals()

  // Update the value of the configurator if it changed.
  // TODO: Document why this needs to happen as part of the render loop and not
  // in an effect.
  if (configuratorJSON !== configuratorJSONSignal.value) {
    configuratorJSONSignal.value = configuratorJSON
    configuratorSignal.value = ConfiguratorEncoder.decode(
      JSON.parse(configuratorJSONSignal.value),
    )
  }

  // We put this outside of the useSignalEffect to not trigger the effect when
  // properties used change.
  const handleRewrite = useCallback(() => {
    const pathname = window.location.pathname
    const currentStep = configuratorSignal.value.currentStep.value
    if (isUndefined(currentStep)) {
      return
    }
    const stepName = currentStep.name.value

    if (isUndefined(stepName)) {
      return
    }

    const ids = configuratorSignal.value.selection.value.encodeToQueryString(
      configuratorSignal.value.selection.value.current.value,
    )

    const url = new URL(window.location.toString())
    url.pathname = pathname
    url.searchParams.set('step', stepName)
    url.searchParams.set('ids', ids)

    const urlAsString = decodeURIComponent(url.toString())

    if (urlAsString === window.location.href) {
      return
    }

    log.debug({
      method: 'ClientConfigurator:handleRewrite',
      stepName,
      ids,
      url,
      urlAsString,
      href: window.location.href,
      urlIsSameAsHref: urlAsString === window.location.href,
    })

    window.history.pushState({}, '', urlAsString)
  }, [])

  let searchParams: URLSearchParams
  if (isBrowser()) {
    searchParams = new URLSearchParams(window.location.search)
  } else {
    searchParams = new URLSearchParams()
  }

  useEffect(() => {
    log.debug({
      method: 'ClientConfigurator:useEffect',
      searchParams,
    })

    if (searchParams.size === 0) {
      handleRewrite()
    }
  }, [handleRewrite, searchParams])

  log.debug({
    method: 'ClientConfigurator',
  })

  return children
}
